import { useCreateChannel } from "api/channels/service";
import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { formatPhoneNumber, validatePhoneNumber } from "../../../utils/phoneUtils";
import { useGetCountries } from "api/countries/service";
import { useTranslation } from "react-i18next";
import { getChannelTypeOptions } from "../../../constants/channelTypes";
import Select from "components/Select";
import { useNotification } from "../../../contexts/NotificationContext";

const ChannelsEmbedded = ({
  activeColor = "blue",
  defaultType = "inventory_buyer",
  onClose,
  onCreated,
  noCard = false,
  isDisabled = true,
  isTheme2 = false,
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const selectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const inputStyle = isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined;
  const controlClass = (...parts) => classNames(isTheme2 && "form-control", ...parts);

  const createChannelMutation = useCreateChannel(undefined, { suppressNavigate: true });
  const { data: countriesData } = useGetCountries();
  const countryList = countriesData?.data || [];

  const typeOptions = useMemo(() => getChannelTypeOptions(t), [t]);

  const statusOptions = [
    { value: 'active', label: t('channelsEmbedded.status.active') },
    { value: 'inactive', label: t('channelsEmbedded.status.inactive') },
    { value: 'pending', label: t('channelsEmbedded.status.pending') },
  ];

  const countryOptions = [{ value: '', label: t('channelsEmbedded.selectCountry') }, ...countryList.map(c => ({ value: String(c.id), label: c.name }))];

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: defaultType || 'inventory_buyer',
    status: 'active',
    fed_id: '',
    legal_name: '',
    address_1: '',
    address_2: '',
    zip: '',
    city: '',
    state: '',
    county: '',
    country: '',
    contact_person: '',
    phone_1: '',
    phone_2: '',
    fax_1: '',
    fax_2: '',
    website: '',
    email: '',
    text_name: '',
    accounting_class: '',
  });
  const [errors, setErrors] = useState({});

  // Country → State: compute after formData exists
  const selectedCountry = useMemo(() => countryList.find(c => String(c.id) === String(formData.country)), [countryList, formData.country]);
  const stateOptions = selectedCountry?.states || [];
  const hasStates = Array.isArray(stateOptions) && stateOptions.length > 0;


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let formattedValue = value;
    if (name.includes('phone') || name.includes('fax')) {
      formattedValue = formatPhoneNumber(value);
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : formattedValue,
      ...(name === 'country' ? { state: '' } : {}),
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const missingFields = [];

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Name is required';
      missingFields.push(t("channelsAdd.descriptiveName"));
    }
    if (!formData.legal_name || !formData.legal_name.trim()) {
      newErrors.legal_name = 'Legal name is required';
      missingFields.push(t("channelsAdd.legalName"));
    }
    if (!formData.address_1 || !formData.address_1.trim()) {
      newErrors.address_1 = 'Address 1 is required';
      missingFields.push(t("channelsAdd.address1"));
    }
    if (!formData.zip || !formData.zip.trim()) {
      newErrors.zip = 'ZIP is required';
      missingFields.push(t("channelsAdd.zip"));
    }
    if (!formData.city) {
      newErrors.city = 'City is required';
      missingFields.push(t("channelsAdd.city"));
    }
    const requireState = hasStates;
    if (requireState && !formData.state) {
      newErrors.state = 'State is required';
      missingFields.push(t("channelsAdd.state"));
    }
    if (!formData.country) {
      newErrors.country = 'Country is required';
      missingFields.push(t("channelsAdd.country"));
    }
    if (!formData.phone_1 || !formData.phone_1.trim()) {
      newErrors.phone_1 = 'Phone 1 is required';
      missingFields.push(t("channelsAdd.phone1"));
    } else {
      const phoneError = validatePhoneNumber(formData.phone_1);
      if (phoneError) {
        newErrors.phone_1 = phoneError;
        missingFields.push(t("channelsAdd.phone1"));
      }
    }

    setErrors(newErrors);

    // Show notification if there are errors
    if (Object.keys(newErrors).length > 0) {
      const fieldsList = missingFields.join(', ');
      showNotification(
        'danger',
        `${t('channelsAdd.requiredFieldsMissing')}: ${fieldsList}`
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const created = await createChannelMutation.mutateAsync({
        name: formData.name,
        type: formData.type,
        description: formData.description,
        channel_status: formData.status,
        fed_id: formData.fed_id,
        legal_name: formData.legal_name,
        address_1: formData.address_1,
        address_2: formData.address_2,
        zip: formData.zip,
        city: formData.city === '' ? null : formData.city,
        county: formData.county,
        contact_person: formData.contact_person,
        phone_1: formData.phone_1,
        phone_2: formData.phone_2,
        fax_1: formData.fax_1,
        fax_2: formData.fax_2,
        website: formData.website,
        email: formData.email,
        text_name: formData.text_name,
        accounting_class: formData.accounting_class,
      });
      if (onCreated) onCreated(created);
      if (onClose) onClose();
    } catch (err) {
      // notifications handled in hook
    }
  };

  const handleCancel = () => {
    if (onClose) onClose();
  };

  const renderForm = () => (
    <Form onSubmit={handleSubmit} className={classNames({ "channels-embedded-fields": isTheme2 })}>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label for="type" className="form-control-label">{t('channelsEmbedded.type')}</Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              placeholder={t('channelsEmbedded.selectType')}
              name="type"
              isSearchable={false}
              closeMenuOnSelect={true}
              value={formData.type ? {
                value: formData.type,
                label: typeOptions.find(opt => opt.value === formData.type)?.label || formData.type
              } : null}
              onChange={(option) => {
                const syntheticEvent = {
                  target: {
                    name: 'type',
                    value: option?.value || ''
                  }
                };
                handleInputChange(syntheticEvent);
              }}
              options={typeOptions}
              isDisabled={isDisabled}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="name" className="form-control-label">{t('channelsEmbedded.descriptiveName')} <span style={{ color: 'red' }}>*</span></Label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              className={controlClass(errors.name && "is-invalid")}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterChannelName')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="description" className="form-control-label">{t('channelsEmbedded.description')}</Label>
            <Input
              id="description"
              name="description"
              type="text"
              value={formData.description}
              onChange={handleInputChange}
              className={controlClass(errors.description && "is-invalid")}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterDescription')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="fed_id" className="form-control-label">{t('channelsEmbedded.fedId')}</Label>
            <Input
              id="fed_id"
              name="fed_id"
              type="text"
              value={formData.fed_id}
              onChange={handleInputChange}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterFedId')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="legal_name" className="form-control-label">{t('channelsEmbedded.legalName')} <span style={{ color: 'red' }}>*</span></Label>
            <Input
              id="legal_name"
              name="legal_name"
              type="text"
              value={formData.legal_name}
              onChange={handleInputChange}
              className={controlClass(errors.legal_name && "is-invalid")}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterLegalName')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="address_1" className="form-control-label">{t('channelsEmbedded.address1')} <span style={{ color: 'red' }}>*</span></Label>
            <Input
              id="address_1"
              name="address_1"
              type="text"
              value={formData.address_1}
              onChange={handleInputChange}
              className={controlClass(errors.address_1 && "is-invalid")}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterAddress1')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="address_2" className="form-control-label">{t('channelsEmbedded.address2')}</Label>
            <Input
              id="address_2"
              name="address_2"
              type="text"
              value={formData.address_2}
              onChange={handleInputChange}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterAddress2')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label for="zip" className="form-control-label">{t('channelsEmbedded.zip')} <span style={{ color: 'red' }}>*</span></Label>
            <Input
              id="zip"
              name="zip"
              type="text"
              value={formData.zip}
              onChange={handleInputChange}
              className={controlClass(errors.zip && "is-invalid")}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterZip')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="city" className="form-control-label">{t('channelsEmbedded.city')} <span style={{ color: 'red' }}>*</span></Label>
            <Input
              id="city"
              name="city"
              type="text"
              value={formData.city}
              onChange={handleInputChange}
              className={controlClass(errors.city && "is-invalid")}
              style={inputStyle}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="state" className="form-control-label">{t('channelsEmbedded.state')} <span style={{ color: 'red' }}>*</span></Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              placeholder={
                !formData.country ? t('channelsEmbedded.selectCountryFirst') :
                  formData.country && !hasStates ? t('channelsEmbedded.noStates') :
                    t('channelsEmbedded.selectState')
              }
              name="state"
              isSearchable={false}
              closeMenuOnSelect={true}
              value={formData.state ? {
                value: formData.state,
                label: stateOptions.find(opt => String(opt.id) === formData.state)?.name || formData.state
              } : null}
              onChange={(option) => {
                const syntheticEvent = {
                  target: {
                    name: 'state',
                    value: option?.value || ''
                  }
                };
                handleInputChange(syntheticEvent);
              }}
              options={hasStates ? stateOptions.map(option => ({
                value: String(option.id),
                label: option.name
              })) : []}
              isDisabled={createChannelMutation.isPending || !hasStates}
              isClearable
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="county" className="form-control-label">{t('channelsEmbedded.county')}</Label>
            <Input
              id="county"
              name="county"
              type="text"
              value={formData.county}
              onChange={handleInputChange}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterCounty')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="country" className="form-control-label">{t('channelsEmbedded.country')} <span style={{ color: 'red' }}>*</span></Label>
            <Select
              activeColor={activeColor}
              styles={selectStyles}
              className="react-select"
              placeholder={t('channelsEmbedded.selectCountry')}
              name="country"
              isSearchable
              closeMenuOnSelect={true}
              value={formData.country ? {
                value: formData.country,
                label: countryOptions.find(opt => opt.value === formData.country)?.label || formData.country
              } : null}
              onChange={(option) => {
                const syntheticEvent = {
                  target: {
                    name: 'country',
                    value: option?.value || ''
                  }
                };
                handleInputChange(syntheticEvent);
              }}
              options={countryOptions}
              isDisabled={createChannelMutation.isPending}
              isClearable
            />
          </FormGroup>
        </Col>
      </Row>

      <h5 className="mt-2 text-white">{t('channelsEmbedded.contactInformation')}</h5>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label for="contact_person" className="form-control-label">{t('channelsEmbedded.contactPerson')}</Label>
            <Input
              id="contact_person"
              name="contact_person"
              type="text"
              value={formData.contact_person}
              onChange={handleInputChange}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterContactPerson')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="phone_1" className="form-control-label">{t('channelsEmbedded.phone1')} <span style={{ color: 'red' }}>*</span></Label>
            <Input
              id="phone_1"
              name="phone_1"
              type="text"
              value={formData.phone_1}
              onChange={handleInputChange}
              className={controlClass(errors.phone_1 && "is-invalid")}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterPhoneNumber')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="phone_2" className="form-control-label">{t('channelsEmbedded.phone2')}</Label>
            <Input
              id="phone_2"
              name="phone_2"
              type="text"
              value={formData.phone_2}
              onChange={handleInputChange}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterPhoneNumber')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label for="fax_1" className="form-control-label">{t('channelsEmbedded.fax1')}</Label>
            <Input
              id="fax_1"
              name="fax_1"
              type="text"
              value={formData.fax_1}
              onChange={handleInputChange}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterFaxNumber')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="fax_2" className="form-control-label">{t('channelsEmbedded.fax2')}</Label>
            <Input
              id="fax_2"
              name="fax_2"
              type="text"
              value={formData.fax_2}
              onChange={handleInputChange}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterFaxNumber')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="website" className="form-control-label">{t('channelsEmbedded.website')}</Label>
            <Input
              id="website"
              name="website"
              type="url"
              value={formData.website}
              onChange={handleInputChange}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterWebsite')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="email" className="form-control-label">{t('channelsEmbedded.email')}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterEmail')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
      </Row>

      <h5 className="mt-2 text-white">{t('channelsEmbedded.additionalInformation')}</h5>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="text_name" className="form-control-label">{t('channelsEmbedded.textName')}</Label>
            <Input
              id="text_name"
              name="text_name"
              type="text"
              value={formData.text_name}
              onChange={handleInputChange}
              maxLength={12}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterTextName')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="accounting_class" className="form-control-label">{t('channelsEmbedded.accountingClass')}</Label>
            <Input
              id="accounting_class"
              name="accounting_class"
              type="text"
              value={formData.accounting_class}
              onChange={handleInputChange}
              className={controlClass()}
              style={inputStyle}
              placeholder={t('channelsEmbedded.enterAccountingClass')}
              disabled={createChannelMutation.isPending}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="12">
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              color="secondary"
              onClick={handleCancel}
              disabled={createChannelMutation.isPending}
              className="px-4"
            >
              {t('channelsEmbedded.cancel')}
            </Button>
            <Button
              style={{ ...activeColorStyle, color: 'white' }}
              type="submit"
              disabled={createChannelMutation.isPending}
              className="px-4"
            >
              {createChannelMutation.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('channelsEmbedded.creating')}
                </>
              ) : (
                <>
                  <i className="tim-icons icon-simple-add" style={{ marginRight: '8px' }} />
                  {t('channelsEmbedded.createChannel')}
                </>
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );

  if (noCard) {
    return (
      <div className="p-2">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h4 style={{ margin: 0 }}>
            <i className="tim-icons icon-chart-bar-32" style={{ marginRight: '10px' }} />
            {t('channelsEmbedded.title')}
          </h4>
          <Button
            color="secondary"
            size="sm"
            onClick={handleCancel}
            disabled={createChannelMutation.isPending}
          >
            <i className="tim-icons icon-simple-remove" style={{ marginRight: '5px' }} />
            {t('channelsEmbedded.cancel')}
          </Button>
        </div>
        {renderForm()}
      </div>
    );
  }

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <CardTitle tag="h4" style={{ margin: 0 }}>
                  <i className="tim-icons icon-chart-bar-32" style={{ marginRight: '10px' }} />
                  {t('channelsEmbedded.title')}
                </CardTitle>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={createChannelMutation.isPending}
                >
                  <i className="tim-icons icon-simple-remove" style={{ marginRight: '5px' }} />
                  {t('channelsEmbedded.cancel')}
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {renderForm()}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ChannelsEmbedded;
